"use client";

import {
	useRouter,
	useSearchParams,
} from "@/infrastructure/router/navigation";
import {
	type CSSProperties,
	type KeyboardEvent as ReactKeyboardEvent,
	type PointerEvent as ReactPointerEvent,
	type WheelEvent as ReactWheelEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	ARTIST_CANVAS_CONTRACT_VERSION,
	loadProjectCanvasKernel,
	type ProjectCanvasAssetKind,
	type ProjectCanvasDocument,
	type ProjectCanvasKernel,
	type ProjectCanvasKernelLoader,
	type ProjectCanvasNode,
} from "@/domain/projectCanvas/ProjectCanvasDocumentBoundary";
import { canConnectProjectCanvasAssets } from "@/domain/projectCanvas/ProjectCanvasConnectionPolicy";
import {
	canonicalProjectId,
	loadProjectCanvasDocument,
	submitProjectEdgeCreate,
	submitProjectNodeCreate,
	submitProjectNodeDelete,
	submitProjectNodeMove,
	subscribeProjectOperations,
	type ProjectCanvasAcceptedOperation,
} from "@/infrastructure/supabase/projectCanvas";
import { GlassIconButton } from "../design-system/buttons/GlassIconButton";
import { ArtistIcon } from "../design-system/icons/ArtistIcon";
import { Skeleton } from "../design-system/loading/Skeleton";
import { SheetHeader } from "../design-system/sheets/SheetHeader";
import { EmptyState } from "../design-system/states/EmptyState";

// Geometry — mirrors iOS ProjectCanvasViewGeometry.
// screenPoint = viewportSize/2 + panOffset + worldPoint * zoomScale.
const MIN_ZOOM = 0.35;
const MAX_ZOOM = 2.4;
const GRID_STEP = 80;
const GRID_PADDING = 260;
const MINIMAP_SIZE = 96;

const ASSET_KIND_LABELS: Readonly<Record<ProjectCanvasAssetKind, string>> = {
	text: "文本",
	lyrics: "歌词",
	music: "音乐",
	tts: "语音",
	image: "图像",
	video: "视频",
	asset_3d: "3D 资产",
};

const ASSET_KIND_ICON: Readonly<
	Record<
		ProjectCanvasAssetKind,
		"doc" | "music" | "waveform" | "image" | "play" | "create"
	>
> = {
	text: "doc",
	lyrics: "doc",
	music: "music",
	tts: "waveform",
	image: "image",
	video: "play",
	asset_3d: "create",
};

const TTS_WAVEFORM_HEIGHTS = [
	18, 34, 52, 30, 64, 42, 24, 58, 74, 46, 30, 62, 40, 22, 50, 68, 36, 20,
] as const;

const TTS_WAVEFORM_BARS: readonly { readonly id: string; readonly height: number }[] =
	TTS_WAVEFORM_HEIGHTS.map((height, index) => ({
		id: `tts-wave-bar-${index}`,
		height,
	}));

const EDGE_COLOR: Readonly<
	Record<ProjectCanvasDocument["edges"][number]["relationship"], string>
> = {
	input: "color-mix(in srgb, cyan 62%, transparent)",
	reference: "color-mix(in srgb, orange 62%, transparent)",
	generated: "color-mix(in srgb, var(--ds-accent) 72%, transparent)",
};

const ADD_MENU_ITEMS: ReadonlyArray<{
	readonly kind: ProjectCanvasAssetKind;
	readonly label: string;
	readonly icon: "doc" | "music" | "waveform" | "image" | "play" | "create";
}> = [
	{ kind: "text", label: "文本", icon: "doc" },
	{ kind: "lyrics", label: "歌词", icon: "doc" },
	{ kind: "music", label: "音乐", icon: "music" },
	{ kind: "tts", label: "语音", icon: "waveform" },
	{ kind: "image", label: "图像", icon: "image" },
	{ kind: "video", label: "视频", icon: "play" },
	{ kind: "asset_3d", label: "3D 资产", icon: "create" },
];

type KernelState = "loading" | "ready" | "unavailable";

interface Viewport {
	readonly width: number;
	readonly height: number;
	readonly panX: number;
	readonly panY: number;
	readonly zoom: number;
}

interface DragState {
	readonly nodeId: string;
	readonly startPointerX: number;
	readonly startPointerY: number;
	readonly startWorldX: number;
	readonly startWorldY: number;
}

interface PanState {
	readonly startPointerX: number;
	readonly startPointerY: number;
	readonly startPanX: number;
	readonly startPanY: number;
}

export function ProjectCanvasWorkspace({
	loader,
	initialDocument,
	readOnly = false,
}: Readonly<{
	loader?: ProjectCanvasKernelLoader;
	initialDocument?: ProjectCanvasDocument;
	readOnly?: boolean;
}>) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const remoteProjectId =
		readOnly || initialDocument !== undefined
			? null
			: canonicalProjectId(searchParams.get("project"));
	const [kernel, setKernel] = useState<ProjectCanvasKernel | null>(null);
	const [kernelState, setKernelState] = useState<KernelState>(
		readOnly ? "ready" : "loading",
	);
	const [document, setDocument] = useState<ProjectCanvasDocument>(() =>
		initialDocument ?? createEmptyDocument(),
	);
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
	const [connectionSourceNodeId, setConnectionSourceNodeId] = useState<string | null>(null);
	const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [showAddMenu, setShowAddMenu] = useState(false);
	const [viewport, setViewport] = useState<Viewport>({
		width: 0,
		height: 0,
		panX: 0,
		panY: 0,
		zoom: 0.72,
	});
	const [dragState, setDragState] = useState<DragState | null>(null);
	const [panState, setPanState] = useState<PanState | null>(null);
	const [didFitInitial, setDidFitInitial] = useState(false);
	const [undoStack, setUndoStack] = useState<ProjectCanvasDocument[]>([]);
	const [redoStack, setRedoStack] = useState<ProjectCanvasDocument[]>([]);
	const [_remoteState, setRemoteState] = useState<
		"local" | "loading" | "ready" | "unavailable"
	>(remoteProjectId === null ? "local" : "loading");

	const containerRef = useRef<HTMLDivElement | null>(null);
	const operationCounter = useRef(0);
	const clientIdRef = useRef(`web:${crypto.randomUUID().toLowerCase()}`);
	const spacePressedRef = useRef(false);
	const handleBack = useCallback(() => {
		if (window.history.length > 1) {
			router.back();
			return;
		}
		router.replace("/discover");
	}, [router]);

	// Load the locked KMP kernel.
	useEffect(() => {
		if (readOnly) {
			setKernel(null);
			setKernelState("ready");
			return undefined;
		}
		let active = true;
		setKernelState("loading");
		loadProjectCanvasKernel(loader)
			.then((value) => {
				if (!active) return;
				setKernel(value);
				setKernelState("ready");
			})
			.catch(() => {
				if (!active) return;
				setKernel(null);
				setKernelState("unavailable");
			});
		return () => {
			active = false;
		};
	}, [loader, readOnly]);

	useEffect(() => {
		if (remoteProjectId === null || kernel === null) {
			setRemoteState("local");
			return undefined;
		}
		let active = true;
		let loaded = false;
		let subscriptionReady = false;
		const pendingOperations: ProjectCanvasAcceptedOperation[] = [];
		setRemoteState("loading");
		const reduceAccepted = (
			current: ProjectCanvasDocument,
			accepted: ProjectCanvasAcceptedOperation,
		): ProjectCanvasDocument => {
			const result = kernel.reduce(
				current,
				accepted.acceptedAtEpochMilliseconds,
				accepted.operation,
			);
			if (result.outcome.kind === "failure") {
				throw new Error(result.outcome.error.code);
			}
			return result.finalState;
		};
		const startLoad = () => {
			if (!active || !subscriptionReady) return;
			void loadProjectCanvasDocument(remoteProjectId, kernel)
				.then((baseDocument) => {
					if (!active) return;
					const replayed = pendingOperations
						.toSorted(
							(left, right) =>
								left.operation.serverSequence - right.operation.serverSequence,
						)
						.reduce(reduceAccepted, baseDocument);
					loaded = true;
					pendingOperations.length = 0;
					setDocument(replayed);
					setUndoStack([]);
					setRedoStack([]);
					setDidFitInitial(false);
					setRemoteState("ready");
				})
				.catch(() => {
					if (active) setRemoteState("unavailable");
				});
		};
		const channel = subscribeProjectOperations(
			remoteProjectId,
			(accepted) => {
				if (!active) return;
				if (!loaded) {
					pendingOperations.push(accepted);
					return;
				}
				setDocument((current) => {
					try {
						return reduceAccepted(current, accepted);
					} catch {
						setRemoteState("unavailable");
						return current;
					}
				});
			},
			() => {
				subscriptionReady = true;
				startLoad();
			},
		);
		return () => {
			active = false;
			void channel.unsubscribe();
		};
	}, [kernel, remoteProjectId]);

	const reduceAcceptedOperation = useCallback(
		(
			current: ProjectCanvasDocument,
			accepted: ProjectCanvasAcceptedOperation,
		): ProjectCanvasDocument => {
			if (kernel === null) throw new Error("canvas-kernel-unavailable");
			const result = kernel.reduce(
				current,
				accepted.acceptedAtEpochMilliseconds,
				accepted.operation,
			);
			if (result.outcome.kind === "failure") {
				throw new Error(result.outcome.error.code);
			}
			return result.finalState;
		},
		[kernel],
	);

	const applyAcceptedOperation = useCallback(
		(accepted: ProjectCanvasAcceptedOperation) => {
			setDocument((current) => reduceAcceptedOperation(current, accepted));
		},
		[reduceAcceptedOperation],
	);

	const recoverRemoteDocument = useCallback(async (): Promise<void> => {
		if (remoteProjectId === null || kernel === null) return;
		setRemoteState("loading");
		try {
			const loaded = await loadProjectCanvasDocument(remoteProjectId, kernel);
			setDocument(loaded);
			setUndoStack([]);
			setRedoStack([]);
			setRemoteState("ready");
		} catch {
			setRemoteState("unavailable");
		}
	}, [kernel, remoteProjectId]);

	// Measure viewport via ResizeObserver.
	useEffect(() => {
		const element = containerRef.current;
		if (element === null) return undefined;
		const update = () => {
			const rect = element.getBoundingClientRect();
			setViewport((previous) => ({
				...previous,
				width: rect.width,
				height: rect.height,
			}));
		};
		update();
		const observer = new ResizeObserver(update);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	const activeNodes = useMemo(
		() =>
			document.nodes.filter((node) => node.deletedAtEpochMilliseconds === null),
		[document],
	);
	const activeEdges = useMemo(
		() =>
			document.edges.filter((edge) => edge.deletedAtEpochMilliseconds === null),
		[document],
	);
	const nodesById = useMemo(() => {
		const map = new Map<string, ProjectCanvasNode>();
		for (const node of activeNodes) map.set(node.id, node);
		return map;
	}, [activeNodes]);

	const _selectedNode =
		activeNodes.find((node) => node.id === selectedNodeId) ?? null;
	const detailNode =
		activeNodes.find((node) => node.id === detailNodeId) ?? null;
	const detailGeneration = useMemo(
		() =>
			detailNode === null
				? null
				: (document.generationRecords.find(
						(record) => record.outputNodeId === detailNode.id,
					) ?? null),
		[detailNode, document.generationRecords],
	);

	// Visible nodes culling (matches iOS refreshVisibleNodes).
	const visibleWorldRect = useMemo(() => {
		const scale = Math.max(viewport.zoom, 0.05);
		return {
			minX: -viewport.width / 2 / scale - viewport.panX / scale - GRID_PADDING,
			minY: -viewport.height / 2 / scale - viewport.panY / scale - GRID_PADDING,
			maxX: (viewport.width / 2 - viewport.panX) / scale + GRID_PADDING,
			maxY: (viewport.height / 2 - viewport.panY) / scale + GRID_PADDING,
		};
	}, [viewport]);

	const visibleNodes = useMemo(() => {
		if (activeNodes.length === 0) return [];
		return activeNodes.filter((node) => {
			const left = node.position.x;
			const right = node.position.x + node.size.width;
			const top = node.position.y;
			const bottom = node.position.y + node.size.height;
			return (
				right >= visibleWorldRect.minX &&
				left <= visibleWorldRect.maxX &&
				bottom >= visibleWorldRect.minY &&
				top <= visibleWorldRect.maxY
			);
		});
	}, [activeNodes, visibleWorldRect]);

	const visibleEdges = useMemo(() => {
		if (activeEdges.length === 0) return [];
		return activeEdges.filter((edge) => {
			const source = nodesById.get(edge.sourceNodeId);
			const target = nodesById.get(edge.targetNodeId);
			if (source === undefined || target === undefined) return false;
			const left = Math.min(source.position.x, target.position.x);
			const right = Math.max(
				source.position.x + source.size.width,
				target.position.x + target.size.width,
			);
			const top = Math.min(source.position.y, target.position.y);
			const bottom = Math.max(
				source.position.y + source.size.height,
				target.position.y + target.size.height,
			);
			return (
				right >= visibleWorldRect.minX &&
				left <= visibleWorldRect.maxX &&
				bottom >= visibleWorldRect.minY &&
				top <= visibleWorldRect.maxY
			);
		});
	}, [activeEdges, nodesById, visibleWorldRect]);

	// Fit initial viewport to content bounds — mirrors iOS fitInitialViewportIfNeeded.
	const fitViewport = useCallback(() => {
		if (viewport.width === 0 || viewport.height === 0) return;
		if (activeNodes.length === 0) {
			setViewport((previous) => ({
				...previous,
				panX: 0,
				panY: 0,
				zoom: 0.72,
			}));
			return;
		}
		let minX = Number.POSITIVE_INFINITY;
		let minY = Number.POSITIVE_INFINITY;
		let maxX = Number.NEGATIVE_INFINITY;
		let maxY = Number.NEGATIVE_INFINITY;
		for (const node of activeNodes) {
			minX = Math.min(minX, node.position.x);
			minY = Math.min(minY, node.position.y);
			maxX = Math.max(maxX, node.position.x + node.size.width);
			maxY = Math.max(maxY, node.position.y + node.size.height);
		}
		const padding = 160;
		const width = maxX - minX + padding * 2;
		const height = maxY - minY + padding * 2;
		const scale = Math.min(
			viewport.width / Math.max(width, 1),
			viewport.height / Math.max(height, 1),
			MAX_ZOOM,
		);
		const zoom = Math.max(MIN_ZOOM, scale);
		const centerX = (minX + maxX) / 2;
		const centerY = (minY + maxY) / 2;
		setViewport((previous) => ({
			...previous,
			zoom,
			panX: -centerX * zoom,
			panY: -centerY * zoom,
		}));
	}, [viewport.width, viewport.height, activeNodes]);

	useEffect(() => {
		if (didFitInitial) return;
		if (viewport.width === 0 || viewport.height === 0) return;
		if (activeNodes.length === 0) return;
		fitViewport();
		setDidFitInitial(true);
	}, [
		didFitInitial,
		viewport.width,
		viewport.height,
		activeNodes.length,
		fitViewport,
	]);

	// Coordinate helpers (iOS ProjectCanvasViewGeometry.screenPoint).
	const worldToScreen = useCallback(
		(worldX: number, worldY: number) => ({
			x: viewport.width / 2 + viewport.panX + worldX * viewport.zoom,
			y: viewport.height / 2 + viewport.panY + worldY * viewport.zoom,
		}),
		[viewport],
	);

	// Zoom toward a screen anchor (iOS ViewModel.zoom).
	const zoomToward = useCallback(
		(nextZoom: number, screenX: number, screenY: number) => {
			setViewport((previous) => {
				const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom));
				if (clamped === previous.zoom) return previous;
				const world = {
					x: (screenX - previous.width / 2 - previous.panX) / previous.zoom,
					y: (screenY - previous.height / 2 - previous.panY) / previous.zoom,
				};
				return {
					...previous,
					zoom: clamped,
					panX: screenX - previous.width / 2 - world.x * clamped,
					panY: screenY - previous.height / 2 - world.y * clamped,
				};
			});
		},
		[],
	);

	// Wheel zoom ( ctrlKey ⇒ pinch-zoom trackpad gesture ).
	const handleWheel = useCallback(
		(event: ReactWheelEvent<HTMLDivElement>) => {
			if (event.ctrlKey || event.metaKey) {
				event.preventDefault();
				const delta = -event.deltaY * 0.01;
				const factor = Math.exp(delta);
				const rect = event.currentTarget.getBoundingClientRect();
				const screenX = event.clientX - rect.left;
				const screenY = event.clientY - rect.top;
				zoomToward(viewport.zoom * factor, screenX, screenY);
				return;
			}
			event.preventDefault();
			setViewport((previous) => ({
				...previous,
				panX: previous.panX - event.deltaX,
				panY: previous.panY - event.deltaY,
			}));
		},
		[viewport.zoom, zoomToward],
	);

	// Wheel listener needs to be non-passive to preventDefault.
	useEffect(() => {
		const element = containerRef.current;
		if (element === null) return undefined;
		const handler = (event: WheelEvent) => {
			if (event.ctrlKey || event.metaKey) {
				event.preventDefault();
				const delta = -event.deltaY * 0.01;
				const factor = Math.exp(delta);
				const rect = element.getBoundingClientRect();
				const screenX = event.clientX - rect.left;
				const screenY = event.clientY - rect.top;
				zoomToward(viewport.zoom * factor, screenX, screenY);
				return;
			}
			if (event.deltaX !== 0 || event.deltaY !== 0) {
				event.preventDefault();
				setViewport((previous) => ({
					...previous,
					panX: previous.panX - event.deltaX,
					panY: previous.panY - event.deltaY,
				}));
			}
		};
		element.addEventListener("wheel", handler, { passive: false });
		return () => element.removeEventListener("wheel", handler);
	}, [viewport.zoom, zoomToward]);

	// Background pointer pan.
	const handleBackgroundPointerDown = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (event.pointerType === "mouse" && event.button !== 0 && event.button !== 1) return;
			event.currentTarget.focus({ preventScroll: true });
			const rect = event.currentTarget.getBoundingClientRect();
			const screenX = event.clientX - rect.left;
			const screenY = event.clientY - rect.top;
			setPanState({
				startPointerX: screenX,
				startPointerY: screenY,
				startPanX: viewport.panX,
				startPanY: viewport.panY,
			});
			event.currentTarget.setPointerCapture(event.pointerId);
		},
		[viewport.panX, viewport.panY],
	);

	const handleBackgroundPointerMove = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (panState === null) return;
			const rect = event.currentTarget.getBoundingClientRect();
			const screenX = event.clientX - rect.left;
			const screenY = event.clientY - rect.top;
			setViewport((previous) => ({
				...previous,
				panX: panState.startPanX + (screenX - panState.startPointerX),
				panY: panState.startPanY + (screenY - panState.startPointerY),
			}));
		},
		[panState],
	);

	const handleBackgroundPointerUp = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (panState !== null) {
				setPanState(null);
			}
			if (event.currentTarget.hasPointerCapture(event.pointerId)) {
				event.currentTarget.releasePointerCapture(event.pointerId);
			}
		},
		[panState],
	);

	// Node pointer interactions.
	const handleNodePointerDown = useCallback(
		(event: ReactPointerEvent<HTMLButtonElement>) => {
			if (
				event.pointerType === "mouse" &&
				(event.button === 1 || (event.button === 0 && spacePressedRef.current))
			) {
				// Let middle-button or Space+left-button bubble to the viewport pan handler,
				// even when the pointer starts on top of a card.
				return;
			}
			// Always stop propagation so background pan does not trigger when
			// tapping a node (even in read-only mode).
			event.stopPropagation();
			if (readOnly || !isEditMode || kernel === null) return;
			if (event.button !== 0 && event.pointerType === "mouse") return;
			const node = activeNodes.find(
				(value) => value.id === event.currentTarget.dataset.nodeId,
			);
			if (node === undefined) return;
			const rect = containerRef.current?.getBoundingClientRect();
			if (rect === undefined) return;
			setDragState({
				nodeId: node.id,
				startPointerX: event.clientX - rect.left,
				startPointerY: event.clientY - rect.top,
				startWorldX: node.position.x,
				startWorldY: node.position.y,
			});
			event.currentTarget.setPointerCapture(event.pointerId);
		},
		[readOnly, isEditMode, kernel, activeNodes],
	);

	const handleNodePointerMove = useCallback(
		(event: ReactPointerEvent<HTMLButtonElement>) => {
			if (dragState === null) return;
			const rect = containerRef.current?.getBoundingClientRect();
			if (rect === undefined) return;
			const screenX = event.clientX - rect.left;
			const screenY = event.clientY - rect.top;
			const deltaX = (screenX - dragState.startPointerX) / viewport.zoom;
			const deltaY = (screenY - dragState.startPointerY) / viewport.zoom;
			const nextX = dragState.startWorldX + deltaX;
			const nextY = dragState.startWorldY + deltaY;
			setDocument((previous) =>
				applyLocalMove(previous, dragState.nodeId, nextX, nextY),
			);
		},
		[dragState, viewport.zoom],
	);

	const commitMoveThroughKernel = useCallback(
		(nodeId: string, currentDocument: ProjectCanvasDocument) => {
			if (kernel === null) return;
			const movedNode = currentDocument.nodes.find(
				(node) => node.id === nodeId,
			);
			if (movedNode === undefined) return;
			operationCounter.current += 1;
			const sequence = currentDocument.version + 1;
			try {
				const result = kernel.reduce(
					currentDocument,
					currentDocument.updatedAtEpochMilliseconds + 1_000,
					{
						kind: "move-node",
						projectId: currentDocument.project.id,
						actorUserId: "user:web-canvas",
						idempotencyKey: `web-canvas-move-${operationCounter.current}`,
						clientId: "client:web-canvas",
						clientSequence: sequence,
						serverSequence: sequence,
						nodeId,
						position: movedNode.position,
					},
				);
				if (result.outcome.kind === "success") {
					setUndoStack((stack) => [...stack, currentDocument]);
					setRedoStack([]);
					if (remoteProjectId === null) {
						setDocument(result.finalState);
					} else {
						void submitProjectNodeMove({
							projectId: remoteProjectId,
							nodeId,
							x: movedNode.position.x,
							y: movedNode.position.y,
							clientId: clientIdRef.current,
							clientSequence: operationCounter.current,
							idempotencyKey: `web:${clientIdRef.current}:${operationCounter.current}`,
						})
							.then(applyAcceptedOperation)
							.catch(() => void recoverRemoteDocument());
					}
				}
			} catch {
				setKernel(null);
				setKernelState("unavailable");
			}
		},
		[
			applyAcceptedOperation,
			kernel,
			recoverRemoteDocument,
			remoteProjectId,
		],
	);

	const handleNodePointerUp = useCallback(
		(event: ReactPointerEvent<HTMLButtonElement>) => {
			if (dragState === null) return;
			const wasDragging =
				Math.abs(event.movementX) > 0 || Math.abs(event.movementY) > 0;
			const nodeId = dragState.nodeId;
			const finalDocument = document;
			setDragState(null);
			if (event.currentTarget.hasPointerCapture(event.pointerId)) {
				event.currentTarget.releasePointerCapture(event.pointerId);
			}
			if (!wasDragging) return;
			// Commit through KMP kernel for boundary validation.
			commitMoveThroughKernel(nodeId, finalDocument);
		},
		[dragState, document, commitMoveThroughKernel],
	);

	const handleNodeClick = useCallback(
		(nodeId: string) => {
			if (dragState !== null) return;
			if (connectionSourceNodeId !== null) {
				const source = activeNodes.find((node) => node.id === connectionSourceNodeId);
				const target = activeNodes.find((node) => node.id === nodeId);
				if (
					source === undefined ||
					target === undefined ||
					source.id === target.id ||
					!canConnectProjectCanvasAssets(source.assetKind, target.assetKind)
				) {
					setConnectionSourceNodeId(null);
					return;
				}
				operationCounter.current += 1;
				const clientSequence = operationCounter.current;
				const metadata = {
					projectId: document.project.id,
					clientId: clientIdRef.current,
					clientSequence,
					idempotencyKey: `web:${clientIdRef.current}:${clientSequence}`,
				};
				if (remoteProjectId === null) {
					if (kernel !== null) {
						const result = kernel.reduce(document, Date.now(), {
							kind: "create-edge",
							...metadata,
							actorUserId: "user:web-canvas",
							serverSequence: 0,
							sourceNodeId: source.id,
							targetNodeId: target.id,
							relationship: "reference",
						});
						if (result.outcome.kind === "success") {
							setUndoStack((stack) => [...stack, document]);
							setRedoStack([]);
							setDocument(result.finalState);
						}
					}
				} else {
					void submitProjectEdgeCreate({
						...metadata,
						sourceNodeId: source.id,
						targetNodeId: target.id,
						relationship: "reference",
					})
						.then(applyAcceptedOperation)
						.catch(() => void recoverRemoteDocument());
				}
				setConnectionSourceNodeId(null);
				setSelectedNodeId(target.id);
				return;
			}
			if (selectedNodeId === nodeId) {
				setDetailNodeId(nodeId);
				return;
			}
			setSelectedNodeId(nodeId);
			setDetailNodeId(null);
		},
		[
			activeNodes,
			applyAcceptedOperation,
			connectionSourceNodeId,
			document,
			dragState,
			kernel,
			recoverRemoteDocument,
			remoteProjectId,
			selectedNodeId,
		],
	);

	// Add blank node (iOS createBlankCard / createBlankNodeLocally).
	const handleAddNode = useCallback(
		(assetKind: ProjectCanvasAssetKind) => {
			if (readOnly || kernel === null) {
				setShowAddMenu(false);
				return;
			}
			const centerX = -viewport.panX / viewport.zoom;
			const centerY = -viewport.panY / viewport.zoom;
			const zIndex =
				activeNodes.reduce((max, node) => Math.max(max, node.zIndex), -1) + 1;
			const nodeId = remoteProjectId === null
				? `canvas:blank_${Date.now().toString(36)}`
				: crypto.randomUUID().toLowerCase();
			const now = Date.now();
			const blankNode: ProjectCanvasNode = {
				id: nodeId,
				projectId: document.project.id,
				assetKind,
				title: { kind: "literal", value: ASSET_KIND_LABELS[assetKind] },
				subtitle: null,
				position: { x: centerX, y: centerY },
				size: { width: 220, height: 180 },
				zIndex,
				mediaUrl: null,
				thumbnailUrl: null,
				widthPixels: null,
				heightPixels: null,
				durationMilliseconds: null,
				format: null,
				textContent:
					assetKind === "text" || assetKind === "lyrics" ? "新卡片" : null,
				createdAtEpochMilliseconds: now,
				updatedAtEpochMilliseconds: now,
				deletedAtEpochMilliseconds: null,
			};
			operationCounter.current += 1;
			const clientSequence = operationCounter.current;
			const metadata = {
				projectId: document.project.id,
				clientId: clientIdRef.current,
				clientSequence,
				idempotencyKey: `web:${clientIdRef.current}:${clientSequence}`,
			};
			if (remoteProjectId === null) {
				const result = kernel.reduce(document, now, {
					kind: "create-node",
					...metadata,
					actorUserId: "user:web-canvas",
					serverSequence: 0,
					nodeId,
					assetKind,
					position: blankNode.position,
					zIndex,
				});
				if (result.outcome.kind === "success") {
					setUndoStack((stack) => [...stack, document]);
					setRedoStack([]);
					setDocument(result.finalState);
				}
			} else {
				void submitProjectNodeCreate({ ...metadata, node: blankNode })
					.then(applyAcceptedOperation)
					.catch(() => void recoverRemoteDocument());
			}
			setSelectedNodeId(nodeId);
			setShowAddMenu(false);
		},
		[
			kernel,
			viewport.panX,
			viewport.panY,
			viewport.zoom,
			activeNodes,
			applyAcceptedOperation,
			document,
			readOnly,
			recoverRemoteDocument,
			remoteProjectId,
		],
	);

	const handleUndo = useCallback(() => {
		setUndoStack((stack) => {
			if (stack.length === 0) return stack;
			const previous = stack[stack.length - 1];
			if (previous === undefined) return stack;
			setRedoStack((redo) => [...redo, document]);
			setDocument(previous);
			return stack.slice(0, -1);
		});
	}, [document]);

	const handleRedo = useCallback(() => {
		setRedoStack((stack) => {
			if (stack.length === 0) return stack;
			const next = stack[stack.length - 1];
			if (next === undefined) return stack;
			setUndoStack((undo) => [...undo, document]);
			setDocument(next);
			return stack.slice(0, -1);
		});
	}, [document]);

	const deleteNode = useCallback((nodeId: string) => {
		if (kernel === null) return;
		operationCounter.current += 1;
		const clientSequence = operationCounter.current;
		const metadata = {
			projectId: document.project.id,
			clientId: clientIdRef.current,
			clientSequence,
			idempotencyKey: `web:${clientIdRef.current}:${clientSequence}`,
		};
		if (remoteProjectId === null) {
			const result = kernel.reduce(document, Date.now(), {
				kind: "delete-node",
				...metadata,
				actorUserId: "user:web-canvas",
				serverSequence: 0,
				nodeId,
			});
			if (result.outcome.kind === "success") {
				setUndoStack((stack) => [...stack, document]);
				setRedoStack([]);
				setDocument(result.finalState);
			}
		} else {
			void submitProjectNodeDelete({ ...metadata, nodeId })
				.then(applyAcceptedOperation)
				.catch(() => void recoverRemoteDocument());
		}
		setConnectionSourceNodeId((source) => source === nodeId ? null : source);
		setSelectedNodeId(null);
		setDetailNodeId(null);
	}, [
		applyAcceptedOperation,
		document,
		kernel,
		recoverRemoteDocument,
		remoteProjectId,
	]);

	const handleDeleteDetailNode = useCallback(() => {
		if (detailNodeId !== null) deleteNode(detailNodeId);
	}, [detailNodeId, deleteNode]);

	const handleResetZoom = useCallback(() => {
		fitViewport();
	}, [fitViewport]);

	const handleCanvasKeyDown = useCallback(
		(event: ReactKeyboardEvent<HTMLDivElement>) => {
			const target = event.target as HTMLElement;
			if (
				target instanceof HTMLInputElement ||
				target instanceof HTMLTextAreaElement ||
				target.isContentEditable
			) return;

			const command = event.metaKey || event.ctrlKey;
			if (command && event.key.toLowerCase() === "z") {
				event.preventDefault();
				if (event.shiftKey) handleRedo();
				else handleUndo();
				return;
			}

			if (event.code === "Space") {
				event.preventDefault();
				spacePressedRef.current = true;
				return;
			}

			const step = event.shiftKey ? 96 : 32;
			if (event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "ArrowUp" || event.key === "ArrowDown") {
				event.preventDefault();
				setViewport((previous) => ({
					...previous,
					panX: previous.panX + (event.key === "ArrowLeft" ? step : event.key === "ArrowRight" ? -step : 0),
					panY: previous.panY + (event.key === "ArrowUp" ? step : event.key === "ArrowDown" ? -step : 0),
				}));
				return;
			}

			if (event.key === "+" || event.key === "=") {
				event.preventDefault();
				zoomToward(viewport.zoom * 1.12, viewport.width / 2, viewport.height / 2);
				return;
			}
			if (event.key === "-") {
				event.preventDefault();
				zoomToward(viewport.zoom / 1.12, viewport.width / 2, viewport.height / 2);
				return;
			}
			if (event.key === "0") {
				event.preventDefault();
				fitViewport();
				return;
			}
			if (event.key === "Escape") {
				event.preventDefault();
				setPanState(null);
				setDragState(null);
				setShowAddMenu(false);
				setDetailNodeId(null);
				return;
			}
			if (
				!readOnly &&
				isEditMode &&
				selectedNodeId !== null &&
				(event.key === "Delete" || event.key === "Backspace")
			) {
				event.preventDefault();
				deleteNode(selectedNodeId);
			}
		},
		[
			deleteNode,
			fitViewport,
			handleRedo,
			handleUndo,
			isEditMode,
			readOnly,
			selectedNodeId,
			viewport.height,
			viewport.width,
			viewport.zoom,
			zoomToward,
		],
	);

	const handleCanvasKeyUp = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
		if (event.code === "Space") spacePressedRef.current = false;
	}, []);

	const projectTitle = document.project.title.value;

	// Loading / unavailable / empty states.
	if (kernelState === "loading") {
		return (
			<section
				className="canvas-page canvas-page--loading"
				aria-label="项目画布"
			>
				<CanvasTopBar
					title={projectTitle}
					isEditMode={false}
					onBack={handleBack}
					onEditToggle={() => {}}
					editDisabled
				/>
				<div className="canvas-loading" aria-live="polite">
					<Skeleton shape="rectangle" width="60%" height={120} />
					<Skeleton shape="rectangle" width="40%" height={80} />
				</div>
			</section>
		);
	}

	if (kernelState === "unavailable") {
		return (
			<section className="canvas-page canvas-page--error" aria-label="项目画布">
				<CanvasTopBar
					title={projectTitle}
					isEditMode={false}
					onBack={handleBack}
					onEditToggle={() => {}}
					editDisabled
				/>
				<EmptyState
					title="画布核心未接入"
					message={`KMP ${ARTIST_CANVAS_CONTRACT_VERSION} 规则核心尚未就绪。已拒绝旧 artifact 与 TypeScript 替代实现；请稍后重试。`}
					symbol={<ArtistIcon name="info" size={36} />}
					actionLabel="重试"
					actionSymbol={<ArtistIcon name="back" size={16} />}
					onAction={() => {
						if (typeof window !== "undefined") window.location.reload();
					}}
				/>
			</section>
		);
	}

	if (activeNodes.length === 0) {
		return (
			<section className="canvas-page canvas-page--empty" aria-label="项目画布">
				<CanvasTopBar
					title={projectTitle}
					isEditMode={false}
					onBack={handleBack}
					onEditToggle={() => {}}
					editDisabled={readOnly}
				/>
				<EmptyState
					title="画布尚无节点"
					message={readOnly ? "这个共享项目目前没有可展示的卡片。" : "双击空白处或使用下方工具栏的添加按钮，创建第一张卡片。"}
					symbol={<ArtistIcon name="create" size={36} />}
					{...(readOnly
						? {}
						: {
								actionLabel: "添加节点",
								actionSymbol: <ArtistIcon name="plus" size={16} />,
								onAction: () => setShowAddMenu(true),
							})}
				/>
				<CanvasBottomToolbar
					readOnly={readOnly}
					isEditMode={isEditMode}
					canConnect={false}
					isConnecting={false}
					zoomPercent={Math.round(viewport.zoom * 100)}
					canUndo={remoteProjectId === null && undoStack.length > 0}
					canRedo={remoteProjectId === null && redoStack.length > 0}
					onAdd={() => setShowAddMenu(true)}
					onToggleSelect={() => setIsEditMode((value) => !value)}
					onConnect={() => {}}
					onResetZoom={handleResetZoom}
					onUndo={handleUndo}
					onRedo={handleRedo}
				/>
				<AddNodeDialog
					open={!readOnly && showAddMenu}
					onClose={() => setShowAddMenu(false)}
					onSelect={handleAddNode}
				/>
			</section>
		);
	}

	return (
		<section
			className="canvas-page"
			aria-label={`项目画布：${projectTitle}`}
			style={
				{
					"--canvas-pan-x": `${viewport.panX}px`,
					"--canvas-pan-y": `${viewport.panY}px`,
					"--canvas-zoom": viewport.zoom,
				} as CSSProperties
			}
		>
			<CanvasTopBar
				title={projectTitle}
				isEditMode={readOnly ? false : isEditMode}
				onBack={handleBack}
				onEditToggle={() => {
					if (!readOnly) setIsEditMode((value) => !value);
				}}
				editDisabled={readOnly}
			/>

			<div
				ref={containerRef}
				className="canvas-viewport"
				role="application"
				aria-label="桌面项目画布。拖动空白处或使用中键平移，滚轮移动，按住 Control 或 Command 滚轮缩放；方向键平移，零键适配画布。"
				// biome-ignore lint/a11y/noNoninteractiveTabindex: this keyboard-operated application surface must receive focus.
				tabIndex={0}
				onPointerDown={handleBackgroundPointerDown}
				onPointerMove={handleBackgroundPointerMove}
				onPointerUp={handleBackgroundPointerUp}
				onPointerCancel={handleBackgroundPointerUp}
				onWheel={handleWheel}
				onKeyDown={handleCanvasKeyDown}
				onKeyUp={handleCanvasKeyUp}
				style={{
					touchAction: "none",
					cursor: panState !== null ? "grabbing" : "default",
				}}
			>
				<CanvasGrid viewport={viewport} visibleWorldRect={visibleWorldRect} />

				<CanvasEdges
					edges={visibleEdges}
					nodesById={nodesById}
					worldToScreen={worldToScreen}
				/>

				{visibleNodes.map((node) => {
					const screen = worldToScreen(node.position.x, node.position.y);
					const width = node.size.width * viewport.zoom;
					const height = node.size.height * viewport.zoom;
					const isSelected = node.id === selectedNodeId;
					return (
						<CanvasNodeCard
							key={node.id}
							node={node}
							isSelected={isSelected}
							left={screen.x}
							top={screen.y}
							width={width}
							height={height}
							zoom={viewport.zoom}
							isEditMode={!readOnly && isEditMode}
							onPointerDown={handleNodePointerDown}
							onPointerMove={handleNodePointerMove}
							onPointerUp={handleNodePointerUp}
							onClick={() => handleNodeClick(node.id)}
						/>
					);
				})}

				<CanvasMiniMap
					nodes={activeNodes}
					viewport={viewport}
					selectedNodeId={selectedNodeId}
				/>

				<div className="canvas-zoom-badge" aria-live="polite">
					{Math.round(viewport.zoom * 100)}%
				</div>
			</div>

			<CanvasBottomToolbar
				readOnly={readOnly}
				isEditMode={isEditMode}
				canConnect={selectedNodeId !== null}
				isConnecting={connectionSourceNodeId !== null}
				zoomPercent={Math.round(viewport.zoom * 100)}
				canUndo={remoteProjectId === null && undoStack.length > 0}
				canRedo={remoteProjectId === null && redoStack.length > 0}
				onAdd={() => setShowAddMenu(true)}
				onToggleSelect={() => setIsEditMode((value) => !value)}
				onConnect={() => {
					setConnectionSourceNodeId((current) =>
						current === null ? selectedNodeId : null,
					);
				}}
				onResetZoom={handleResetZoom}
				onUndo={handleUndo}
				onRedo={handleRedo}
			/>

			<AddNodeDialog
				open={!readOnly && showAddMenu}
				onClose={() => setShowAddMenu(false)}
				onSelect={handleAddNode}
			/>

			<NodeDetailDialog
				node={detailNode}
				generation={detailGeneration}
				canDelete={!readOnly && isEditMode}
				onClose={() => setDetailNodeId(null)}
				onDelete={handleDeleteDetailNode}
			/>
		</section>
	);
}

function CanvasTopBar({
	title,
	isEditMode,
	editDisabled,
	onBack,
	onEditToggle,
}: Readonly<{
	title: string;
	isEditMode: boolean;
	editDisabled: boolean;
	onBack: () => void;
	onEditToggle: () => void;
}>) {
	return (
		// biome-ignore lint/a11y/useAriaPropsSupportedByRole: nested header loses implicit banner role; aria-label remains useful for assistive tech
		<header className="canvas-topbar" aria-label="画布顶部导航">
			<button
				type="button"
				className="ds-icon-button canvas-topbar__back"
				aria-label="返回"
				onClick={onBack}
			>
				<ArtistIcon name="back" size={22} />
			</button>
			<h1 className="canvas-topbar__title">{title}</h1>
			<button
				type="button"
				className="ds-icon-button canvas-topbar__edit"
				aria-label={isEditMode ? "退出编辑模式" : "进入编辑模式"}
				aria-pressed={isEditMode}
				disabled={editDisabled}
				onClick={onEditToggle}
			>
				<ArtistIcon name={isEditMode ? "create" : "search"} size={20} />
			</button>
		</header>
	);
}

function CanvasBottomToolbar({
	readOnly,
	isEditMode,
	canConnect,
	isConnecting,
	zoomPercent,
	canUndo,
	canRedo,
	onAdd,
	onToggleSelect,
	onConnect,
	onResetZoom,
	onUndo,
	onRedo,
}: Readonly<{
	readOnly: boolean;
	isEditMode: boolean;
	canConnect: boolean;
	isConnecting: boolean;
	zoomPercent: number;
	canUndo: boolean;
	canRedo: boolean;
	onAdd: () => void;
	onToggleSelect: () => void;
	onConnect: () => void;
	onResetZoom: () => void;
	onUndo: () => void;
	onRedo: () => void;
}>) {
	return (
		<nav className="canvas-toolbar" role="toolbar" aria-label="画布工具">
			{readOnly ? null : (
				<>
					<GlassIconButton label="添加节点" kind="accent" onClick={onAdd}>
						<ArtistIcon name="plus" size={22} />
					</GlassIconButton>
					<GlassIconButton
						label={isEditMode ? "切换到选择模式" : "切换到编辑模式"}
						aria-pressed={isEditMode}
						onClick={onToggleSelect}
						style={isEditMode ? { color: "var(--ds-accent)" } : undefined}
					>
						<ArtistIcon name={isEditMode ? "create" : "search"} size={20} />
					</GlassIconButton>
					<GlassIconButton
						label={isConnecting ? "取消添加关联线" : "添加关联线"}
						disabled={!canConnect}
						aria-pressed={isConnecting}
						onClick={onConnect}
						style={isConnecting ? { color: "var(--ds-accent)" } : undefined}
					>
						<ArtistIcon name="chevron" size={20} />
					</GlassIconButton>
				</>
			)}
			<GlassIconButton label="适合画布" onClick={onResetZoom}>
				<ArtistIcon name="image" size={20} />
			</GlassIconButton>
			<span className="canvas-toolbar__zoom" aria-live="polite">
				{zoomPercent}%
			</span>
			{readOnly ? null : (
				<>
					<GlassIconButton label="撤销" disabled={!canUndo} onClick={onUndo}>
						<ArtistIcon name="back" size={20} />
					</GlassIconButton>
					<GlassIconButton label="重做" disabled={!canRedo} onClick={onRedo}>
						<ArtistIcon
							name="chevron"
							size={20}
							style={{ transform: "scaleX(-1)" } as CSSProperties}
						/>
					</GlassIconButton>
				</>
			)}
		</nav>
	);
}

function CanvasGrid({
	viewport,
	visibleWorldRect,
}: Readonly<{
	viewport: Viewport;
	visibleWorldRect: { minX: number; minY: number; maxX: number; maxY: number };
}>) {
	if (viewport.width === 0 || viewport.height === 0) return null;
	const scale = Math.max(viewport.zoom, 0.05);
	const startX = Math.floor(visibleWorldRect.minX / GRID_STEP) * GRID_STEP;
	const startY = Math.floor(visibleWorldRect.minY / GRID_STEP) * GRID_STEP;
	const lineWidth = Math.max(0.5, Math.min(1.25, viewport.zoom));
	const lines: React.ReactNode[] = [];
	let x = startX;
	let verticalIndex = Math.floor(startX / GRID_STEP);
	while (x <= visibleWorldRect.maxX) {
		const screenX = viewport.width / 2 + viewport.panX + x * scale;
		const isMajor = verticalIndex % 4 === 0;
		lines.push(
			<line
				key={`v-${verticalIndex}`}
				x1={screenX}
				y1={0}
				x2={screenX}
				y2={viewport.height}
				stroke={`rgb(255 255 255 / ${isMajor ? 0.11 : 0.055})`}
				strokeWidth={lineWidth}
			/>,
		);
		x += GRID_STEP;
		verticalIndex += 1;
	}
	let y = startY;
	let horizontalIndex = Math.floor(startY / GRID_STEP);
	while (y <= visibleWorldRect.maxY) {
		const screenY = viewport.height / 2 + viewport.panY + y * scale;
		const isMajor = horizontalIndex % 4 === 0;
		lines.push(
			<line
				key={`h-${horizontalIndex}`}
				x1={0}
				y1={screenY}
				x2={viewport.width}
				y2={screenY}
				stroke={`rgb(255 255 255 / ${isMajor ? 0.11 : 0.055})`}
				strokeWidth={lineWidth}
			/>,
		);
		y += GRID_STEP;
		horizontalIndex += 1;
	}
	return (
		<svg
			className="canvas-grid"
			width={viewport.width}
			height={viewport.height}
			viewBox={`0 0 ${viewport.width} ${viewport.height}`}
			aria-hidden="true"
		>
			<defs>
				<linearGradient id="canvas-bg-gradient" x1="0" y1="0" x2="1" y2="1">
					<stop offset="0%" stopColor="rgb(5 7 9)" />
					<stop offset="100%" stopColor="rgb(1 2 3)" />
				</linearGradient>
			</defs>
			<rect
				x={0}
				y={0}
				width={viewport.width}
				height={viewport.height}
				fill="url(#canvas-bg-gradient)"
			/>
			{lines}
		</svg>
	);
}

function CanvasEdges({
	edges,
	nodesById,
	worldToScreen,
}: Readonly<{
	edges: readonly ProjectCanvasDocument["edges"][number][];
	nodesById: Map<string, ProjectCanvasNode>;
	worldToScreen: (worldX: number, worldY: number) => { x: number; y: number };
}>) {
	if (edges.length === 0) return null;
	return (
		<svg className="canvas-edges" aria-hidden="true">
			{edges.map((edge) => {
				const source = nodesById.get(edge.sourceNodeId);
				const target = nodesById.get(edge.targetNodeId);
				if (source === undefined || target === undefined) return null;
				const sourceCenter = worldToScreen(
					source.position.x + source.size.width / 2,
					source.position.y + source.size.height / 2,
				);
				const targetCenter = worldToScreen(
					target.position.x + target.size.width / 2,
					target.position.y + target.size.height / 2,
				);
				const sourceRect = {
					x: worldToScreen(source.position.x, source.position.y).x,
					y: worldToScreen(source.position.x, source.position.y).y,
					width: source.size.width,
					height: source.size.height,
				};
				const targetRect = {
					x: worldToScreen(target.position.x, target.position.y).x,
					y: worldToScreen(target.position.x, target.position.y).y,
					width: target.size.width,
					height: target.size.height,
				};
				const sourcePoint = edgeEndpoint(
					sourceCenter,
					sourceRect,
					targetCenter,
				);
				const targetPoint = edgeEndpoint(
					targetCenter,
					targetRect,
					sourceCenter,
				);
				const midX = (sourcePoint.x + targetPoint.x) / 2;
				const path = `M ${sourcePoint.x} ${sourcePoint.y} C ${midX} ${sourcePoint.y}, ${midX} ${targetPoint.y}, ${targetPoint.x} ${targetPoint.y}`;
				return (
					<path
						key={edge.id}
						d={path}
						stroke={EDGE_COLOR[edge.relationship]}
						strokeWidth={1.6}
						fill="none"
						strokeLinecap="round"
						strokeLinejoin="round"
						data-edge-id={edge.id}
						data-source-node-id={edge.sourceNodeId}
						data-target-node-id={edge.targetNodeId}
						data-source-x={sourcePoint.x}
						data-source-y={sourcePoint.y}
						data-target-x={targetPoint.x}
						data-target-y={targetPoint.y}
					/>
				);
			})}
		</svg>
	);
}

function edgeEndpoint(
	center: { x: number; y: number },
	rect: { x: number; y: number; width: number; height: number },
	other: { x: number; y: number },
): { x: number; y: number } {
	const dx = other.x - center.x;
	const dy = other.y - center.y;
	if (dx === 0 && dy === 0) return center;
	const halfW = rect.width / 2;
	const halfH = rect.height / 2;
	const absDx = Math.abs(dx);
	const absDy = Math.abs(dy);
	if (absDx * halfH > absDy * halfW) {
		const sign = dx > 0 ? 1 : -1;
		return { x: center.x + sign * halfW, y: center.y + dy * (halfW / absDx) };
	}
	const sign = dy > 0 ? 1 : -1;
	return { x: center.x + dx * (halfH / absDy), y: center.y + sign * halfH };
}

function CanvasNodeCard({
	node,
	isSelected,
	left,
	top,
	width,
	height,
	zoom,
	isEditMode,
	onPointerDown,
	onPointerMove,
	onPointerUp,
	onClick,
}: Readonly<{
	node: ProjectCanvasNode;
	isSelected: boolean;
	left: number;
	top: number;
	width: number;
	height: number;
	zoom: number;
	isEditMode: boolean;
	onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
	onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>) => void;
	onPointerUp: (event: ReactPointerEvent<HTMLButtonElement>) => void;
	onClick: () => void;
}>) {
	const icon = ASSET_KIND_ICON[node.assetKind];
	const kindLabel = ASSET_KIND_LABELS[node.assetKind];
	const title = node.title.value;
	const subtitle = node.subtitle ?? null;
	const thumbnailUrl =
		node.thumbnailUrl ?? (node.assetKind === "image" ? node.mediaUrl : null);
	const parameterSummary =
		node.format ??
		(node.durationMilliseconds !== null
			? `${Math.round(node.durationMilliseconds / 1000)}s`
			: null);
	const showWaveform = node.assetKind === "tts";
	const showVideoBadge = node.assetKind === "video";
	const showTextPreview =
		(node.assetKind === "text" || node.assetKind === "lyrics") &&
		node.textContent !== null;
	return (
		<button
			type="button"
			data-node-id={node.id}
			className={
				isSelected ? "canvas-node canvas-node--selected" : "canvas-node"
			}
			style={
				{
					left: `${left}px`,
					top: `${top}px`,
					width: `${width}px`,
					height: `${height}px`,
					zIndex: node.zIndex,
					"--node-zoom": zoom,
				} as CSSProperties
			}
			aria-label={`${title}，${kindLabel} 节点${isSelected ? "（已选中）" : ""}`}
			aria-pressed={isSelected}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
			onClick={onClick}
		>
			<span className="canvas-node__surface" aria-hidden="true">
				{thumbnailUrl !== null ? (
					// biome-ignore lint/performance/noImgElement: dynamic blob URL on a static-export canvas; next/image cannot optimize unoptimized runtime thumbnails here
					<img
						className="canvas-node__media"
						src={thumbnailUrl}
						alt=""
						loading="lazy"
						draggable={false}
					/>
				) : showTextPreview ? (
					<span className="canvas-node__text">{node.textContent}</span>
				) : showWaveform ? (
					<span className="canvas-node__waveform" aria-hidden="true">
						{TTS_WAVEFORM_BARS.map((bar) => (
							<span
								key={bar.id}
								className="canvas-node__wave-bar"
								style={{ height: `${bar.height}px` }}
							/>
						))}
					</span>
				) : (
					<span className="canvas-node__placeholder" aria-hidden="true">
						<ArtistIcon name={icon} size={Math.max(20, 28 * zoom)} />
					</span>
				)}
				{showVideoBadge ? (
					<span className="canvas-node__video-badge" aria-hidden="true">
						<ArtistIcon name="play" size={12} />
					</span>
				) : null}
			</span>
			<span className="canvas-node__tag canvas-node__tag--title">
				<ArtistIcon name={icon} size={11} />
				<span className="canvas-node__tag-kind">{kindLabel}</span>
				<span className="canvas-node__tag-title">{title}</span>
			</span>
			{parameterSummary !== null ? (
				<span className="canvas-node__tag canvas-node__tag--param">
					{parameterSummary}
				</span>
			) : null}
			{subtitle !== null ? (
				<span className="canvas-node__subtitle">{subtitle}</span>
			) : null}
			{isEditMode ? (
				<span className="canvas-node__edit-hint" aria-hidden="true">
					<ArtistIcon name="create" size={10} />
				</span>
			) : null}
		</button>
	);
}

function CanvasMiniMap({
	nodes,
	viewport,
	selectedNodeId,
}: Readonly<{
	nodes: readonly ProjectCanvasNode[];
	viewport: Viewport;
	selectedNodeId: string | null;
}>) {
	if (nodes.length === 0 || viewport.width === 0) return null;
	let minX = Number.POSITIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;
	for (const node of nodes) {
		minX = Math.min(minX, node.position.x);
		minY = Math.min(minY, node.position.y);
		maxX = Math.max(maxX, node.position.x + node.size.width);
		maxY = Math.max(maxY, node.position.y + node.size.height);
	}
	const padding = 48;
	const boundsWidth = Math.max(maxX - minX + padding * 2, 1);
	const boundsHeight = Math.max(maxY - minY + padding * 2, 1);
	const inset = 7;
	const available = MINIMAP_SIZE - inset * 2;
	const scale = Math.min(available / boundsWidth, available / boundsHeight);
	const offsetX = (MINIMAP_SIZE - boundsWidth * scale) / 2;
	const offsetY = (MINIMAP_SIZE - boundsHeight * scale) / 2;
	const project = (worldX: number, worldY: number) => ({
		x: offsetX + (worldX - minX + padding) * scale,
		y: offsetY + (worldY - minY + padding) * scale,
	});
	const zoom = Math.max(viewport.zoom, 0.05);
	const visibleWorldMinX = (-viewport.width / 2 - viewport.panX) / zoom;
	const visibleWorldMinY = (-viewport.height / 2 - viewport.panY) / zoom;
	const visibleWorldMaxX = (viewport.width / 2 - viewport.panX) / zoom;
	const visibleWorldMaxY = (viewport.height / 2 - viewport.panY) / zoom;
	const viewportTopLeft = project(visibleWorldMinX, visibleWorldMinY);
	const viewportBottomRight = project(visibleWorldMaxX, visibleWorldMaxY);
	const viewportRect = {
		x: viewportTopLeft.x,
		y: viewportTopLeft.y,
		width: Math.max(viewportBottomRight.x - viewportTopLeft.x, 0),
		height: Math.max(viewportBottomRight.y - viewportTopLeft.y, 0),
	};
	return (
		<aside className="canvas-minimap" aria-label="画布缩略图">
			<svg
				width={MINIMAP_SIZE}
				height={MINIMAP_SIZE}
				viewBox={`0 0 ${MINIMAP_SIZE} ${MINIMAP_SIZE}`}
				role="img"
				aria-label="画布缩略图"
			>
				<title>画布缩略图</title>
				<rect
					x={0}
					y={0}
					width={MINIMAP_SIZE}
					height={MINIMAP_SIZE}
					rx={18}
					ry={18}
					fill="rgb(0 0 0 / 0.36)"
				/>
				{nodes.map((node) => {
					const topLeft = project(node.position.x, node.position.y);
					return (
						<rect
							key={node.id}
							x={topLeft.x}
							y={topLeft.y}
							width={Math.max(node.size.width * scale, 2)}
							height={Math.max(node.size.height * scale, 2)}
							rx={Math.min(
								4,
								(Math.min(node.size.width, node.size.height) * scale) / 2,
							)}
							fill={
								node.id === selectedNodeId
									? "color-mix(in srgb, var(--ds-accent) 84%, transparent)"
									: "rgb(255 255 255 / 0.42)"
							}
						/>
					);
				})}
				<rect
					x={viewportRect.x}
					y={viewportRect.y}
					width={viewportRect.width}
					height={viewportRect.height}
					rx={4}
					fill="none"
					stroke="rgb(255 255 255 / 0.82)"
					strokeWidth={1.3}
				/>
			</svg>
		</aside>
	);
}

function AddNodeDialog({
	open,
	onClose,
	onSelect,
}: Readonly<{
	open: boolean;
	onClose: () => void;
	onSelect: (kind: ProjectCanvasAssetKind) => void;
}>) {
	const dialogRef = useRef<HTMLDialogElement | null>(null);
	useEffect(() => {
		const dialog = dialogRef.current;
		if (dialog === null) return;
		if (open && !dialog.open) {
			dialog.showModal();
		} else if (!open && dialog.open) {
			dialog.close();
		}
	}, [open]);
	return (
		<dialog
			ref={dialogRef}
			className="canvas-dialog canvas-dialog--add"
			aria-label="创建卡片"
			onClose={onClose}
		>
			<SheetHeader
				title="创建卡片"
				leading="cancel"
				leadingLabel="取消"
				onLeading={onClose}
			/>
			<div className="canvas-dialog__body">
				<ul className="canvas-add-menu">
					{ADD_MENU_ITEMS.map((item) => (
						<li key={item.kind}>
							<button
								type="button"
								className="canvas-add-menu__item"
								onClick={() => onSelect(item.kind)}
							>
								<span className="canvas-add-menu__icon" aria-hidden="true">
									<ArtistIcon name={item.icon} size={20} />
								</span>
								<span className="canvas-add-menu__label">{item.label}</span>
							</button>
						</li>
					))}
				</ul>
			</div>
		</dialog>
	);
}

function NodeDetailDialog({
	node,
	generation,
	canDelete,
	onClose,
	onDelete,
}: Readonly<{
	node: ProjectCanvasNode | null;
	generation: ProjectCanvasDocument["generationRecords"][number] | null;
	canDelete: boolean;
	onClose: () => void;
	onDelete: () => void;
}>) {
	const dialogRef = useRef<HTMLDialogElement | null>(null);
	useEffect(() => {
		const dialog = dialogRef.current;
		if (dialog === null) return;
		if (node !== null && !dialog.open) {
			dialog.showModal();
		} else if (node === null && dialog.open) {
			dialog.close();
		}
	}, [node]);
	if (node === null) return null;
	const prompt = generation?.prompt ?? null;
	const negativePrompt = generation?.negativePrompt ?? null;
	const provider = generation?.provider ?? null;
	const model = generation?.modelDisplayName ?? generation?.modelKey ?? null;
	const mode = generation?.mode ?? null;
	const configuration = generation?.configurationSummary ?? null;
	const creditsUsed = generation?.creditsUsed ?? null;
	const createdMs = node.createdAtEpochMilliseconds;
	const createdDate = new Date(createdMs);
	const createdLabel = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, "0")}-${String(createdDate.getDate()).padStart(2, "0")} ${String(createdDate.getHours()).padStart(2, "0")}:${String(createdDate.getMinutes()).padStart(2, "0")}`;
	const references = generation?.referenceNodeIds ?? [];
	const focusedNode = generation?.focusedNodeId ?? null;
	return (
		<dialog
			ref={dialogRef}
			className="canvas-dialog canvas-dialog--detail"
			aria-label="节点详情"
			onClose={onClose}
		>
			<SheetHeader
				title={node.title.value}
				leading="cancel"
				leadingLabel="关闭"
				onLeading={onClose}
			/>
			<div className="canvas-dialog__body canvas-detail">
				{prompt !== null ? (
					<section className="canvas-detail__section">
						<h3>提示词</h3>
						<p className="canvas-detail__prompt">{prompt}</p>
					</section>
				) : null}
				{node.textContent !== null && node.assetKind !== "lyrics" ? (
					<section className="canvas-detail__section">
						<h3>内容</h3>
						<p className="canvas-detail__prompt">{node.textContent}</p>
					</section>
				) : null}
				<section className="canvas-detail__section">
					<h3>谱系</h3>
					{generation === null ? (
						<p className="canvas-detail__empty">
							该历史资产没有可用的生成谱系信息。
						</p>
					) : (
						<dl className="canvas-detail__rows">
							{provider !== null ? (
								<DetailRow label="供应商" value={provider} />
							) : null}
							{model !== null ? <DetailRow label="模型" value={model} /> : null}
							{mode !== null ? <DetailRow label="模式" value={mode} /> : null}
							{configuration !== null ? (
								<DetailRow label="配置" value={configuration} />
							) : null}
							{creditsUsed !== null ? (
								<DetailRow label="积分" value={String(creditsUsed)} />
							) : null}
							{focusedNode !== null ? (
								<DetailRow label="聚焦卡片" value={focusedNode} />
							) : null}
							{references.length > 0 ? (
								<DetailRow label="引用" value={references.join(", ")} />
							) : null}
							<DetailRow label="创建时间" value={createdLabel} />
						</dl>
					)}
				</section>
				<section className="canvas-detail__section">
					<h3>元数据</h3>
					<dl className="canvas-detail__rows">
						<DetailRow label="类型" value={ASSET_KIND_LABELS[node.assetKind]} />
						{node.format !== null ? (
							<DetailRow label="格式" value={node.format} />
						) : null}
						{node.durationMilliseconds !== null ? (
							<DetailRow
								label="时长"
								value={`${Math.round(node.durationMilliseconds / 1000)}s`}
							/>
						) : null}
						{node.widthPixels !== null && node.heightPixels !== null ? (
							<DetailRow
								label="尺寸"
								value={`${node.widthPixels}×${node.heightPixels}`}
							/>
						) : null}
					</dl>
				</section>
				{negativePrompt !== null ? (
					<section className="canvas-detail__section">
						<h3>反向提示词</h3>
						<p className="canvas-detail__prompt">{negativePrompt}</p>
					</section>
				) : null}
				{canDelete ? (
					<button
						type="button"
						className="canvas-detail__delete"
						onClick={onDelete}
					>
						<ArtistIcon name="trash" size={16} />
						<span>删除卡片</span>
					</button>
				) : null}
			</div>
		</dialog>
	);
}

function DetailRow({
	label,
	value,
}: Readonly<{ label: string; value: string }>) {
	return (
		<div className="canvas-detail__row">
			<dt>{label}</dt>
			<dd>{value}</dd>
		</div>
	);
}

function applyLocalMove(
	document: ProjectCanvasDocument,
	nodeId: string,
	nextX: number,
	nextY: number,
): ProjectCanvasDocument {
	const now = Date.now();
	return {
		...document,
		nodes: document.nodes.map((node) =>
			node.id === nodeId
				? {
						...node,
						position: { x: nextX, y: nextY },
						updatedAtEpochMilliseconds: now,
					}
				: node,
		),
		updatedAtEpochMilliseconds: now,
	};
}

function createEmptyDocument(): ProjectCanvasDocument {
	const now = Date.now();
	const suffix =
		typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
			? crypto.randomUUID()
			: `${now}`;
	return {
		project: {
			id: `project:canvas:${suffix}`,
			kind: "music",
			title: { kind: "literal", value: "未命名画布" },
			ownerUserId: "local-owner",
			releaseAlbumId: null,
			releaseTrackId: null,
			releaseVideoId: null,
			createdAtEpochMilliseconds: now,
			updatedAtEpochMilliseconds: now,
			deletedAtEpochMilliseconds: null,
		},
		nodes: [],
		edges: [],
		generationRecords: [],
		version: 0,
		updatedAtEpochMilliseconds: now,
	};
}
